/**
 * i18n — EN default, AR available. Sets <html lang/dir>.
 * Components use useT() or tr(); switching language reloads the page so every text (including module constants) follows.
 */
import { createContext, useContext, useEffect, useMemo } from "react";
import en from "./locales/en.json";
import ar from "./locales/ar.json";
import arValues from "./locales/ar_values.json";

const dictionaries = { en, ar };

const readLang = () => {
  try { return localStorage.getItem("lang") === "ar" ? "ar" : "en"; } catch { return "en"; }
};
export const LANG = readLang();

const lookup = (dict, key) => key.split(".").reduce((o, k) => (o ? o[k] : undefined), dict);

export function tr(key, vars) {
  const raw = lookup(dictionaries[LANG], key) ?? lookup(dictionaries.en, key) ?? key;
  if (!vars) return raw;
  return String(raw).replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? `{${k}}`);
}

// Display label for a stored English value (status, method, type...); the value itself is unchanged.
const EN_V = { form_inspection: "Inspection release", form_testdrive: "Test drive", form_authorization: "Sale authorisation", form_owner_auth: "Owner authorisation", form_sale_contract: "Sale contract" };
export function lbl(value) {
  if (value == null) return value;
  if (LANG === "en") {
    if (typeof value !== "string" || !/^[a-z]+(_[a-z]+)*$/.test(value)) return value;
    const s = EN_V[value] || value.replace(/_/g, " ");
    return s.charAt(0).toUpperCase() + s.slice(1);
  }
  return arValues.v[value] ?? value;
}

// Server error text (English) -> Arabic, via the "srv" map; patterns cover messages with values in them.
export function trServer(msg) {
  if (LANG === "en" || !msg) return msg;
  if (arValues.srv[msg]) return arValues.srv[msg];
  for (const [pat, out] of Object.entries(arValues.srv_patterns)) {
    const m = msg.match(new RegExp(`^${pat}$`));
    if (m) return out.replace(/\$(\d)/g, (_, i) => m[Number(i)] ?? "");
  }
  return `${arValues.srv_fallback} (${msg})`;
}

export const VALIDATION_MSG = () => (LANG === "ar" ? arValues.srv_validation : null);

// Stored English text with data inside (notifications): exact or pattern match, otherwise unchanged.
export function trText(msg) {
  if (LANG === "en" || !msg) return msg;
  if (arValues.notif[msg]) return arValues.notif[msg];
  for (const [pat, out] of Object.entries(arValues.notif_patterns)) {
    const m = msg.match(new RegExp(`^${pat}$`));
    if (m) return out.replace(/\$(\d)/g, (_, i) => lbl(m[Number(i)]) ?? "");
  }
  return msg;
}

export function saveLang(lang) {
  try { localStorage.setItem("lang", lang); } catch {}
}

const I18nContext = createContext({ lang: "en", t: (k) => k, setLang: () => {} });

export function I18nProvider({ children }) {
  useEffect(() => {
    document.documentElement.lang = LANG;
    document.documentElement.dir = LANG === "ar" ? "rtl" : "ltr";
  }, []);

  const value = useMemo(() => ({
    lang: LANG,
    t: tr,
    setLang: (l) => {
      if (l === LANG) return;
      saveLang(l);
      window.dispatchEvent(new CustomEvent("lang-change", { detail: l }));
      window.location.reload();
    },
  }), []);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export const useT = () => useContext(I18nContext);
